import React, { useState, useEffect, useCallback } from 'react';
import './apartmentSetup.css';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faUpload, faTrash, faEdit, faCheck } from '@fortawesome/free-solid-svg-icons';
import LoadingPopup from '../../../SharedView/LoadingPopup';
import axios from 'axios';

function ApartmentSetup({ setupPhotos, propertyId }) {
  const [isOpen, setIsOpen] = useState(false); // Modal state
  const [currentIndex, setCurrentIndex] = useState(0); // Index of the clicked thumbnail
  const [selectedFiles, setSelectedFiles] = useState([]); // Store selected files for upload
  const [photos, setPhotos] = useState([]); // Initialize empty state for photos
  const [isEditMode, setIsEditMode] = useState(false); // Manage panel
  const [showUpload, setShowUpload] = useState(false);  // Upload panel — its own button (owner, 2026-09-02)
  const [isDragging, setIsDragging] = useState(false);  // dropzone highlight
  const [isUploading, setIsUploading] = useState(false); // Upload state
  const [feedbackMessage, setFeedbackMessage] = useState('');
  // Manage is a DRAFT session (owner, 2026-09-02: "saving shouldn't be line
  // by line picture by picture but the whole"): notes and report-photo
  // switches edit a local draft, and ONE Save writes every change.
  const [manageDrafts, setManageDrafts] = useState({}); // id -> { description, wantTask }
  const [manageSaving, setManageSaving] = useState(false);


  // Fetch photos from the backend, using useCallback to avoid recreating the function
  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`/api/properties/${propertyId}/setup-photos`);
      setPhotos(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        // No photos found – clear state
        setPhotos([]);
      } else {
        console.error('Error fetching setup photos:', error);
      }
    }
  }, [propertyId]);
  

  // Initialize photos state on component mount
  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Convert photos to a format suitable for ImageGallery (caption in lightbox)
  const images = photos.map((photo) => ({
    original: photo.file_url,
    thumbnail: photo.file_url,
    description: photo.description || undefined,
  }));

  // Open the modal and set the current index to the clicked thumbnail's index
  const openModal = (index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  // Close the modal
  const closeModal = () => {
    setIsOpen(false);
  };

  // ESC to close (matches the client photo viewer)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  /* RE-ENCODE TO JPEG IN THE BROWSER (owner, 2026-09-02): phone photos
     arrive as 6MB HEIC/PNG; a canvas pass caps the longest edge at 2000px
     and writes JPEG 0.85, so uploads are fast and every stored photo is the
     same format. Anything the browser cannot decode (some HEICs outside
     Safari) uploads unchanged rather than failing. */
  const toJpeg = (file) => new Promise((resolve) => {
    if (file.type === 'image/jpeg' && file.size < 1.5 * 1024 * 1024) { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 2000;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (!blob) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  // Handle file selection and add previews
  const addFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/') || /\.heic$/i.test(f.name));
    const converted = await Promise.all(files.map(toJpeg));
    const newFiles = converted.map((file) => ({ file, preview: URL.createObjectURL(file), description: '' }));
    setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };
  const handleFileSelect = (event) => addFiles(event.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  // Update the description on a not-yet-uploaded file
  const setSelectedFileDescription = (index, description) => {
    setSelectedFiles((prev) => prev.map((f, i) => (i === index ? { ...f, description } : f)));
  };

  const openManage = () => {
    const drafts = {};
    photos.forEach((ph) => {
      drafts[ph.id] = { description: ph.description || '', wantTask: !!ph.task_id };
    });
    setManageDrafts(drafts);
    setIsEditMode(true);
  };

  const saveManage = async () => {
    setManageSaving(true);
    let changed = 0;
    let failed = 0;
    for (const ph of photos) {
      const d = manageDrafts[ph.id];
      if (!d) continue;
      const newDesc = d.description.trim();
      if (newDesc !== (ph.description || '')) {
        try {
          await axios.patch(`/api/properties/${propertyId}/photos/${ph.id}/description`, { description: newDesc });
          changed += 1;
        } catch (e) { failed += 1; console.error('note save failed:', e); }
      }
      if (d.wantTask !== !!ph.task_id) {
        try {
          if (d.wantTask) await axios.post(`/api/properties/${propertyId}/photos/${ph.id}/task`);
          else await axios.delete(`/api/properties/${propertyId}/photos/${ph.id}/task`);
          changed += 1;
        } catch (e) { failed += 1; console.error('task toggle failed:', e); }
      }
    }
    await fetchPhotos();
    setManageSaving(false);
    if (failed) {
      setFeedbackMessage(`Saved ${changed} change${changed === 1 ? '' : 's'} — ${failed} failed, check and retry.`);
    } else {
      setIsEditMode(false);
      if (changed) setFeedbackMessage(`Saved ${changed} change${changed === 1 ? '' : 's'}.`);
    }
  };

  // Remove a selected file from preview
  const removePhoto = (index) => {
    const newFiles = [...selectedFiles];
    URL.revokeObjectURL(newFiles[index].preview); // Release memory for preview URL
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setIsUploading(true);
      const metadataPayload = [];

      for (const { file, description } of selectedFiles) {
        // 1. Request pre-signed upload URL
        const res = await axios.post('/api/properties/generate-property-upload-url', {
          property_id: propertyId,
          fileName: file.name,
          fileType: file.type
        });

        const { url, fileKey, fileUrl } = res.data;

        // 2. Upload file directly to DigitalOcean Spaces
        await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'public-read' // Important for Spaces to make it public
          },
          body: file
        });

        // 3. Save for metadata submission
        metadataPayload.push({ fileKey, fileUrl, description: (description || '').trim() });
      }

      // 4. Save metadata to your backend
      await axios.post(`/api/properties/${propertyId}/photos`, {
        files: metadataPayload
      });

      // 5. Refresh, clear, and close the panel — the job is done.
      await fetchPhotos();
      setSelectedFiles([]);
      setShowUpload(false);
      setFeedbackMessage('Photos uploaded successfully!');
    } catch (error) {
      console.error('Error uploading files:', error);
      setFeedbackMessage('Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle photo deletion
  const handleDeletePhoto = async (photoId) => {
    const confirmed = window.confirm('Are you sure you want to delete this photo?');
    if (!confirmed) return;

    try {
      await axios.delete(`/api/properties/${propertyId}/photos/${photoId}`);

      // Update photos state to remove the deleted photo
      setPhotos((prevPhotos) => prevPhotos.filter((photo) => photo.id !== photoId));
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  return (
    <div className="dp-card setup-photos-section">
      <div className="aps-head dp-cardHead">
        <h2>Apartment Standard Setup</h2>
        {/* ONE mode at a time (owner, 2026-09-02): the other button leaves
            while a panel is open, and the open panel offers its own exit —
            Cancel for upload (Save lives at the bottom of its list), Done for
            manage (rows save themselves as they change). */}
        <div className="aps-headBtns">
          {!isEditMode && !showUpload && (
            <>
              <button type="button" className="aps-uploadTop" onClick={() => setShowUpload(true)}>
                <FontAwesomeIcon icon={faUpload} /> Upload photos
              </button>
              <button type="button" className="aps-manageBtn" onClick={openManage}>
                <FontAwesomeIcon icon={faEdit} /> Manage
              </button>
            </>
          )}
          {showUpload && (
            <>
              <button
                type="button"
                className="aps-manageBtn"
                onClick={() => {
                  // Cancel really cancels: the selection dies with the panel
                  // (owner, 2026-09-02) — reopening starts clean.
                  selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
                  setSelectedFiles([]);
                  setShowUpload(false);
                }}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="aps-uploadTop"
                onClick={handleFileUpload}
                disabled={isUploading || selectedFiles.length === 0}
              >
                <FontAwesomeIcon icon={faUpload} />
                {isUploading
                  ? ' Uploading…'
                  : selectedFiles.length > 0
                    ? ` Upload ${selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'}`
                    : ' Upload'}
              </button>
            </>
          )}
          {isEditMode && (
            <>
              <button type="button" className="aps-manageBtn" onClick={() => setIsEditMode(false)} disabled={manageSaving}>
                Cancel
              </button>
              <button type="button" className="aps-uploadTop" onClick={saveManage} disabled={manageSaving}>
                <FontAwesomeIcon icon={faCheck} /> {manageSaving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ---- View: the gallery. Clean thumbnails, captions, the lightbox. ---- */}
      {isEditMode ? null : photos.length > 0 ? (
        <div className="aps-grid">
          {photos.map((photo, index) => (
            <div key={photo.id || index} className="aps-tile">
              <div className="aps-tileImgWrap" onClick={() => openModal(index)}>
                <img src={photo.file_url} alt={`Setup ${index}`} className="aps-tileImg" />
                {photo.task_id && (
                  <span className="aps-badge" title="Cleaner sends a matching photo at each cleaning">
                    <FontAwesomeIcon icon={faCamera} />
                  </span>
                )}
              </div>
              {photo.description && <div className="aps-cap" title={photo.description}>{photo.description}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="no-photos-message">
          <FontAwesomeIcon icon={faCamera} className="placeholder-icon" />
          <p>No setup photos yet — Manage photos to add the standard for this apartment.</p>
        </div>
      )}

      {/* ---- Manage: opens under the gallery. Each photo is a ROW — thumb,
           note, the report-photo switch, delete — instead of controls crammed
           onto grid tiles (owner, 2026-09-02). ---- */}
      {isEditMode && (
        <div className="aps-panel">
          <div className="aps-panel-title">Uploaded photos</div>
          {photos.length === 0 && <div className="aps-muted">Nothing uploaded yet.</div>}
          {photos.map((photo) => (
            <div key={photo.id} className="aps-row">
              <img src={photo.file_url} alt="" className="aps-row-thumb" onClick={() => openModal(photos.indexOf(photo))} />
              <div className="aps-row-main">
                <div className="aps-row-note">
                  <input
                    type="text"
                    maxLength={300}
                    placeholder="Add a note for this photo…"
                    value={manageDrafts[photo.id]?.description ?? ''}
                    onChange={(e) => setManageDrafts((prev) => ({
                      ...prev, [photo.id]: { ...prev[photo.id], description: e.target.value },
                    }))}
                  />
                </div>
                <label className="aps-switch" title="The cleaner is asked to send a matching photo at every cleaning">
                  <input
                    type="checkbox"
                    checked={manageDrafts[photo.id]?.wantTask ?? false}
                    onChange={(e) => setManageDrafts((prev) => ({
                      ...prev, [photo.id]: { ...prev[photo.id], wantTask: e.target.checked },
                    }))}
                  />
                  <span className="aps-switch-track"><span className="aps-switch-dot" /></span>
                  <span className="aps-switch-label">Report photo every visit</span>
                </label>
              </div>
              <button type="button" className="aps-delete" title="Delete this photo" onClick={() => handleDeletePhoto(photo.id)}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          {photos.length > 0 && (
            <div className="aps-panelActions">
              <button type="button" className="aps-manageBtn" onClick={() => setIsEditMode(false)} disabled={manageSaving}>
                Cancel
              </button>
              <button type="button" className="aps-uploadTop" onClick={saveManage} disabled={manageSaving}>
                <FontAwesomeIcon icon={faCheck} /> {manageSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}

        </div>
      )}

      {/* ---- Upload: its own panel, opened by its own button. A dropzone,
           not a lone button from another decade (owner, 2026-09-02). ---- */}
      {showUpload && (
        <div className="aps-panel">
          <input
            id="upload-input"
            type="file"
            multiple
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <div
            className={`aps-dropzone ${isDragging ? 'is-dragging' : ''}`}
            onClick={() => document.getElementById('upload-input').click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
          >
            <FontAwesomeIcon icon={faUpload} className="aps-dropzone-icon" />
            <div className="aps-dropzone-title">Drag &amp; drop photos here</div>
            <div className="aps-dropzone-sub">or click to browse — JPG, PNG, HEIC</div>
          </div>

          {selectedFiles.length > 0 && (
            <>
              {selectedFiles.map((file, index) => (
                <div key={index} className="aps-row aps-row--pending">
                  <img src={file.preview} alt="" className="aps-row-thumb" />
                  <div className="aps-row-main">
                    <div className="aps-row-note">
                      <input
                        type="text"
                        maxLength={300}
                        placeholder="Add a note…"
                        value={file.description}
                        onChange={(e) => setSelectedFileDescription(index, e.target.value)}
                      />
                    </div>
                  </div>
                  <button type="button" className="aps-delete" title="Remove" onClick={() => removePhoto(index)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
              <button type="button" className="aps-confirmBtn" onClick={handleFileUpload} disabled={isUploading}>
                {isUploading ? 'Uploading…' : `Upload ${selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Modal for Viewing Photos — unchanged; the viewer is fine. */}
      {isOpen && (
        <div className="apsModal" role="dialog" aria-modal="true" onClick={closeModal}>
          <button
            type="button"
            className="modal-x"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label="Close"
            title="Close"
          >
            <span aria-hidden="true">×</span>
          </button>

          <div className="apsModal__content" onClick={(e) => e.stopPropagation()}>
            <ImageGallery
              items={images}
              startIndex={currentIndex}
              showThumbnails={false}
              showFullscreenButton={false}
              showPlayButton={false}
              showBullets={false}
              showIndex={true}
              slideDuration={350}
              lazyLoad={true}
              showNav={true}
              renderLeftNav={(onClick, disabled) => (
                <button
                  type="button"
                  className="apsNav apsNav--left"
                  onClick={onClick}
                  disabled={disabled}
                  aria-label="Previous photo"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <path
                      d="M15 6l-6 6 6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              renderRightNav={(onClick, disabled) => (
                <button
                  type="button"
                  className="apsNav apsNav--right"
                  onClick={onClick}
                  disabled={disabled}
                  aria-label="Next photo"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                    <path
                      d="M9 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            />
          </div>
        </div>
      )}


      <LoadingPopup
        isLoading={isUploading}
        feedbackMessage={feedbackMessage}
        onClose={() => setFeedbackMessage('')}
      />
    </div>
  );
}

export default ApartmentSetup;
